import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema, surfaceSchema } from "./_envelope";

/**
 * Controlled vocabulary for the `cta_clicked` **`value`** — i.e. which CTA was
 * tapped. This is the single source of truth: `ActionButton`'s `eventData.action`
 * is typed as `CtaClickedValue`, so a button cannot fire a CTA name that isn't
 * listed here (PE-877).
 *
 * Scope: this governs `value` only. The metadata envelope below is **not**
 * closed — see the note on `source`.
 *
 * Adding a value is non-breaking per TAXONOMY.md (enum widening) — no
 * `event_version` bump. Removing one is breaking.
 */
export const CTA_CLICKED_VALUES = {
  // Paywall / checkout
  START_FREE_TRIAL: "start_free_trial",
  CONTINUE_CHECKOUT: "continue_checkout",
  EXPLORE_PLANS: "explore_plans",
  CONVERSION_DRAWER_CONTINUE_RX: "conversion_drawer_continue_rx",
  PAYWALL_DISMISSED: "paywall_dismissed",
  LEARN_MORE: "learn_more",

  // Visits / care
  START_VISIT: "start_visit",
  VIEW_VISITS: "view_visits",
  CHAT_WITH_PEDIATRICIAN: "chat_with_pediatrician",
  SEND_SYMPTOMS: "send_symptoms",
  CREATE_CARE_PROFILE: "create_care_profile",
  CANCEL_VISIT_REQUEST: "cancel_visit_request",

  // Intake
  START_INTAKE: "start_intake",
  COMPLETE_INTAKE: "complete_intake",
  RESTART_INTAKE: "restart_intake",
  SUBMIT_INTAKE_TO_PROVIDER: "submit_intake_to_provider",
  BACK_TO_INTAKE_CHAT: "back_to_intake_chat",
  INTAKE_HARD_STOP_EXIT: "intake_hard_stop_exit",
  INTAKE_ARM_RETRY: "intake_arm_retry",
  INTAKE_DONE: "intake_done",
  SELECT_CONDITION: "select_condition",
  CANCEL_CONDITION_SELECTION: "cancel_condition_selection",
  /** Generic "advance to the next step" button in a multi-step flow. */
  CONTINUE: "continue",

  // Nightwatch
  SETUP_NIGHTWATCH: "setup_nightwatch",
  ARM_NIGHTWATCH: "arm_nightwatch",
  GO_TO_CHAT: "go_to_chat",

  // Subscription / billing
  MANAGE_SUBSCRIPTION: "manage_subscription",
  REACTIVATE_SUBSCRIPTION: "reactivate_subscription",
  SUB_CANCEL_REACTIVATE: "sub_cancel_reactivate",
  PAYMENT_ERR_MANAGE: "payment_err_manage",

  // Cancel / save flow
  CANCEL_SUBSCRIPTION_INTENT: "cancel_subscription_intent",
  SAVE_OFFER_SHOWN: "save_offer_shown",
  SAVE_OFFER_ACCEPTED: "save_offer_accepted",
  SAVE_OFFER_DECLINED: "save_offer_declined",
  CANCEL_FLOW_CONTINUE: "cancel_flow_continue",
  CANCEL_FLOW_BACK: "cancel_flow_back",
  CANCEL_FLOW_CLAIM_OFFER: "cancel_flow_claim_offer",
  CANCEL_FLOW_CONFIRM_OFFER: "cancel_flow_confirm_offer",
  CANCEL_FLOW_DECLINE: "cancel_flow_decline",
  CANCEL_FLOW_UPDATE_PAYMENT: "cancel_flow_update_payment",
  CANCEL_FLOW_RETRY: "cancel_flow_retry",
  CANCEL_FLOW_ERROR_CLOSE: "cancel_flow_error_close",
  CANCEL_FLOW_SUCCESS_DONE: "cancel_flow_success_done",
  CANCEL_FLOW_CANCELED_DONE: "cancel_flow_canceled_done",

  // Gates / consent / warnings
  DATA_SHARING_CONSENT: "data_sharing_consent",
  PAID_FEATURE_GATE_CONTINUE: "paid_feature_gate_continue",
  NO_CHILDREN_WARNING_DISMISS: "no_children_warning_dismiss",
  ACTIVE_VISIT_WARNING_DISMISS: "active_visit_warning_dismiss",
  UNBORN_CHILD_WARNING_DISMISS: "unborn_child_warning_dismiss",
  DISMISS_BLOCKED_VISIT: "dismiss_blocked_visit",
  REFRESH: "refresh",

  // Notetaker
  NOTETAKER_ATTEST_START_RECORDING: "notetaker_attest_start_recording",
  NOTETAKER_ALLOW_MICROPHONE: "notetaker_allow_microphone",
  NOTETAKER_MIC_NOT_NOW: "notetaker_mic_not_now",
  NOTETAKER_MIC_RETRY: "notetaker_mic_retry",
  NOTETAKER_MIC_BACK: "notetaker_mic_back",
  NOTETAKER_EXPORT_VISIT: "notetaker_export_visit",
  NOTETAKER_DOWNLOAD_AUDIO: "notetaker_download_audio",
  NOTETAKER_PRIVACY_SETTINGS: "notetaker_privacy_settings",
  NOTETAKER_DELETE_VISIT: "notetaker_delete_visit",
  NOTETAKER_DELETE_VISIT_CONFIRM: "notetaker_delete_visit_confirm",
  NOTETAKER_DELETE_VISIT_CANCEL: "notetaker_delete_visit_cancel",
  NOTETAKER_RETRY_TRANSCRIPTION: "notetaker_retry_transcription",
  NOTETAKER_RETRY_SUMMARY: "notetaker_retry_summary",

  // What's Going Around
  WGA_TRY_AGAIN: "wga_try_again",
  WGA_SAVE_ZIP: "wga_save_zip",
  WGA_ZIP_NEXT: "wga_zip_next",
  WGA_ADD_ZIP: "wga_add_zip",
  WGA_CHANGE_ZIP: "wga_change_zip",
  WGA_ABOUT_THIS_DATA: "wga_about_this_data",
  WGA_OPEN_PAGE: "wga_open_page",

  // Feedback / NPS
  CTA_FEEDBACK_SUBMIT: "cta_feedback_submit",
  NPS_SCORE_SUBMIT: "nps_score_submit",
  NPS_FOLLOWUP_SEND: "nps_followup_send",

  /**
   * Component-showcase buttons under `/debug/playbook`. They exist to demo
   * variants, not to measure a funnel — the specific button goes in `source`
   * so the demo pages don't each mint a product CTA name.
   */
  DEBUG_PLAYBOOK: "debug_playbook",
} as const;

export type CtaClickedValue =
  (typeof CTA_CLICKED_VALUES)[keyof typeof CTA_CLICKED_VALUES];

// Derived from the const object above so the two can't drift.
const ctaClickedValues = Object.values(CTA_CLICKED_VALUES) as [
  CtaClickedValue,
  ...CtaClickedValue[],
];

export const ctaClicked = defineEvent({
  eventName: "cta_clicked",
  eventVersion: 2,
  valueSchema: z.enum(ctaClickedValues),
  metadataSchema: baseEnvelopeSchema
    .extend({
      surface: surfaceSchema.optional(),
      /**
       * Intentionally an open string, not an enum — don't read the closed
       * `value` above and assume the whole envelope is governed.
       *
       * `source` is an envelope field shared by every fire site, not an
       * ActionButton concern: it appears at ~122 sites, only ~15 of which
       * arrive via `eventData`. The rest are direct
       * `logEvent({ metadata: { source } })` calls. Closing it is feasible
       * (all values are static literals today) but only becomes *enforceable*
       * by typing `metadata` — i.e. migrating fire sites to the `useLogEvent`
       * path — which is its own piece of work.
       *
       * Until then `source` is where a call site puts a free-form
       * discriminator, including the `/debug/playbook` buttons that share
       * `action: "debug_playbook"` to stay out of the enum.
       */
      source: z.string().optional(),
      product: z.string().optional(),
      billing_period: z.string().optional(),
      button_position: z.enum(["top", "bottom"]).optional(),
      implied_mrr_cents: z.union([z.string(), z.number()]).optional(),
      implied_immediate_revenue_cents: z
        .union([z.string(), z.number()])
        .optional(),
      n_children: z.union([z.string(), z.number()]).optional(),
      unread_count: z.union([z.string(), z.number()]).optional(),
      has_visits: z.union([z.string(), z.boolean()]).optional(),
      journey_id: z.string().optional(),
      home_variant: z.string().optional(),
    })
    .passthrough(),
});
